//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Context } from "../../common/context/Context";
import Select from "react-select";
import { Link, useLocation } from 'react-router-dom';
import { tableData } from "./data.js";
import { Col, Container, Card, Row, Table, Form, Pagination } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, usePagination } from "react-table";
import { ManageClientsActions } from "./manageClientsActions";
import context from "react-bootstrap/esm/AccordionContext";
import ErrorAlertMessage from "../../common/alertMessages/errorAlertMessage";
import { getPageContentLabel } from "../../common/helpers/cmsLabelHelper";
import { getClientsPageCMSContent } from "./manageClientsCmsHelper";

const BasicForm = (data) => {

    const location = useLocation();
    let clientProfileFormLabels = data && data?.cmsData;
    let clientId = data && data?.clientCode;

    let tableData = data && data?.clientsTableData;
    const countryData = tableData && tableData.length > 0 &&
        tableData.find((item) => item.companyId == clientId && item.programCountryCode == location?.state?.countryCode);
    const countryFromAdmin = data && data?.profileData && data?.profileData[0];

    const countryName = countryData && countryData?.programCountry ? countryData?.programCountry : countryFromAdmin?.programCountry;
    const clientName = countryData && countryData?.companyName ? countryData?.companyName : location && location?.state?.clientName;

    const fetchClientProfileLabels = (key) =>
        getPageContentLabel(clientProfileFormLabels, "ClientProfileComponent", key);

    return (
        <Card>
            <Card.Header>
                <Card.Title>  {clientProfileFormLabels?.pageContent["ClientProfileComponent"]?.Title}</Card.Title>
            </Card.Header>
            <Card.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{fetchClientProfileLabels("ClientProfile.ClientName")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder={fetchClientProfileLabels("ClientProfile.ClientName")}
                            readOnly
                            value={clientName && clientName}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{fetchClientProfileLabels("ClientProfile.Country")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="country"
                            placeholder={fetchClientProfileLabels("ClientProfile.ClientCountry")}
                            readOnly
                            value={countryName && countryName}
                        />
                    </Form.Group>
                </Form>
            </Card.Body>
        </Card>
    )
};

function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
}) {
    const count = preFilteredRows.length;

    return (
        <Form.Control
            value={filterValue || ""}
            onChange={(e) => {
                setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
            }}
            placeholder={`Search ${count} records...`}
            className="mt-2"
        />
    );
}

const ColumnFilteringTable = ({ columns, data, cmsData }) => {

    const fetchClientProfileLabels = (key) =>
        getPageContentLabel(cmsData, "ClientProfileComponent", key);

    const fetchCommonComponentLabel = (key) =>
        getPageContentLabel(cmsData, "CommonComponent", key);

    const returnPageIndexText = (currentPage, TotalPage) => {
        const pageText = fetchCommonComponentLabel("Common.Page");
        let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
        return (
            <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
        )
    }


    const filterTypes = React.useMemo(
        () => ({
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: { pageIndex: 0 },
        },
        useFilters, // useFilters!
        useGlobalFilter,// useGlobalFilter!
        usePagination
    );

    return (
        <Card>
            <Card.Header>
                <Card.Title>{fetchClientProfileLabels("ClientProfile.ManagePrograms")}</Card.Title>
            </Card.Header>
            <Card.Body>
                <Table striped bordered {...getTableProps()} responsive>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>
                                        {column.render("Header")}
                                        {/* Render the columns filter UI */}
                                        <div>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Row className="mt-3">
                    <Col md="6" className="d-flex flex-lg-row flex-column">
                        {returnPageIndexText(pageIndex + 1, pageOptions.length)}
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel('Common.Show')}</span>
                            <Form.Select
                                className="d-inline-block w-auto"
                                value={pageSize}
                                aria-label="pageNumber"
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel('Common.GoToPage')}</span>
                            <Form.Control
                                className="d-inline-block"
                                type="number"
                                aria-label="number"
                                min="1"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{ width: "75px" }}
                            />
                        </div>
                    </Col>
                    <Col md="6" className="mt-1">
                        <Pagination className="float-lg-end float-md-end float-left">
                            <Pagination.First
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Prev
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Next
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            />
                            <Pagination.Last
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const Clients = () => {

    const location = useLocation();
    const context = useContext(Context)
    const clientCode = location && location?.state?.clientId;
    const clientsTableData = context.manageClientsTableData && context.manageClientsTableData;

    const [clientProfileCMSData, setClientProfileCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} })
    const [clientProfileData, setClientProfileData] = useState({})
    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })
    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    const fetchClientProfileCMSData = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        const cmsContent = await getClientsPageCMSContent();
        if (cmsContent) {
            setClientProfileCMSData(cmsContent);
        }
        else {
            refErrorMessageAlert.showAlert = true;
            setErrorAlert(refErrorMessageAlert);
            window.scrollTo(0, 0);
        }
    }

    const fetchClientProfile = async () => {

        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        let { clientId } = location && location.state;
        const countryCode = location && location?.state?.countryCode;

        const { personaId } = location && location?.state;

        await ManageClientsActions.GetClientProfile(clientId, countryCode, personaId).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    let resData = res.data && res.data;
                    setClientProfileData(resData);
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const fetchClientProfileLabels = (key) =>
        getPageContentLabel(clientProfileCMSData, "ClientProfileComponent", key);

    const tableColumns = [
        {
            id: 0,
            Header: fetchClientProfileLabels("ClientProfile.ProgramName"),
            accessor: "programTemplate",
            Cell: ({ row, value }) =>
            ((context.permissionsList && context.permissionsList.includes("Clients.viewclientprogramsetup")) ?
                <Link to={'/edit-existing-clients'}
                    state={{ programSetUpId: row?.original?.programSetUpId, clientId: row?.original?.companyId }}>
                    {value} </Link>
                : value),
        },
        {
            id: 1,
            Header: fetchClientProfileLabels("ClientProfile.ProgramClass"),
            accessor: "programClass",
            Filter: false,
            Cell: ({ row, value }) =>
            ((context.permissionsList && context.permissionsList.includes("Clients.viewclientprogramsetup")) ?
                <Link to={'/edit-existing-clients'}
                    state={{ programSetUpId: row?.original?.programSetUpId, clientId: row?.original?.companyId }}>
                    {value} </Link>
                : value),
        },
        {
            id: 2,
            Header: fetchClientProfileLabels("ClientProfile.NumberOfCandidates"),
            accessor: "candidateCount",
            Filter: false,
        },
        {
            id: 3,
            Header: fetchClientProfileLabels("ClientProfile.CustomProgram"),
            accessor: (values) => {
                const { isCustomProgramSetUp } = values;
                return isCustomProgramSetUp ? "Y" : "N"
            },
            Filter: false
        },
    ];

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchClientProfileCMSData()
        fetchClientProfile()
    }, [])

    let { clientId } = location && location.state;
    let { clientName } = location && location.state;
    let clientHeader = fetchClientProfileLabels("ClientProfile.ClientProfileHeader");
    clientHeader = clientHeader.replace("Client Name", clientName).replace("Client Code", clientId)

    return (
        <React.Fragment>
            <Helmet title="Cards" />
            <Container fluid className="p-0 mt-3">
                {errorAlert?.showAlert &&
                    <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                }
                <Row>
                    <Col md="6" lg="12" className="mt-3">
                        <h1>{clientHeader && clientHeader}</h1>
                    </Col>
                </Row>
            </Container>
            <Container fluid className="p-0">
                <Row>
                    <Col md="6" lg="12" className="mt-3">
                        <BasicForm
                            cmsData={clientProfileCMSData && clientProfileCMSData}
                            clientCode={clientCode}
                            clientsTableData={clientsTableData && clientsTableData}
                            profileData={clientProfileData && clientProfileData.length > 0 && clientProfileData}
                        />
                    </Col>
                </Row>
            </Container>
            <Container fluid className="p-0">
                {clientProfileData && clientProfileData.length > 0 ?
                    <ColumnFilteringTable
                        columns={tableColumns && tableColumns}
                        data={clientProfileData}
                        cmsData={clientProfileCMSData && clientProfileCMSData}
                    />
                    : fetchClientProfileLabels("ClientProfile.NoDataLabel")
                }
            </Container>
        </React.Fragment>
    )
};

export default Clients;
